/**
 * TCN design tokens — palette, typography, motion.
 * Mirrors the Tailwind `tcn.*` namespace and the `[data-theme="tcn"]` CSS vars.
 * Used wherever inline styles or computed gradients/shadows are needed
 * (e.g. RingChart stroke colors, Avatar deterministic gradients, glow shadows).
 */
export const TCN = {
  // === Surfaces ===
  bg: '#000000',
  surface1: '#0e0e10',
  surface2: '#161618',
  surface3: '#1f1f23',
  border: 'rgba(255,255,255,0.08)',
  borderStrong: 'rgba(255,255,255,0.14)',

  // === Text ===
  // Contrast (audit fix #37, 2026-04-28) — WCAG AA against surface1 (#0e0e10):
  //   text     #f5f5f7 → 15.7:1 ✓
  //   textDim  #b3b3b8 → ~5.0:1 ✓ (was #a8a8ad = 3.2:1, fail)
  //   textMute #8a8a8f → ~3.5:1 (UI Components 3:1 min, was #6e6e73 = 2.1:1, fail)
  text: '#f5f5f7',
  textDim: '#b3b3b8',
  textMute: '#8a8a8f',

  // === Accents ===
  cyan: '#22d3ee',     // primary (CTAs, focus)
  magenta: '#ff2d6f',  // today's #1 priority, alerts
  green: '#3ddc97',    // positive, target met
  amber: '#ffb547',    // behind pace, warning
  red: '#ff5566',      // at risk, behind <80%
  diamond: '#7df0ff',  // diamonds-only

  // === Typography ===
  fontSans: '-apple-system, BlinkMacSystemFont, "SF Pro Text", Inter, system-ui, sans-serif',
  fontMono: '"SF Mono", "JetBrains Mono", ui-monospace, monospace',

  // === Motion ===
  ease: 'cubic-bezier(.2,.7,.3,1)',
  durBase: '250ms',
  durChart: '1.4s',

  // === Radii ===
  radius: {
    sm: 8,
    md: 12,
    lg: 16,
    xl: 20,
    pill: 999,
  },

  // === Letter spacing — uppercase labels (#64) ===
  // The mockup ui-kit.jsx uses 0.08em on every uppercase SectionLabel / SmallCap;
  // a few components had drifted to 0.05em or 0.06em — collapse them all here
  // so a future tweak is a one-line change.
  tracking: {
    uppercaseLabel: '0.08em',
  },

  // === Signature gradients (mockup ui-kit.jsx) ===
  gradients: {
    // Today's #1 priority alert — magenta-tinted
    priority: 'linear-gradient(160deg, #2a1118, #160a10)',
    // Strategic Focus card — cyan-tinted
    strategic: 'linear-gradient(135deg, #0f1820, #0a0f14)',
    // Pay-out earned headline — green-tinted (mockup module5-payout.jsx:12)
    earned: 'linear-gradient(160deg, #0d1f1a, #060e0c)',
    // Briefing splash background — cyan-tinted top → black (module67-support.jsx:7)
    briefing: 'linear-gradient(180deg, #0a1418 0%, #000 60%)',
  },
};

/**
 * Avatar gradient palette — 6 deterministic pairs.
 * Aligned on mockup ui-kit.jsx:295-302 (sum-of-charCodes hash % 6).
 */
export const AVATAR_GRADIENTS = [
  ['#ff2d6f', '#a855f7'], // magenta → purple
  ['#22d3ee', '#3b82f6'], // cyan → blue
  ['#3ddc97', '#06b6d4'], // green → cyan
  ['#ffb547', '#ff6b6b'], // amber → coral
  ['#a855f7', '#ec4899'], // purple → pink
  ['#06b6d4', '#22d3ee'], // teal → cyan
];

/**
 * Auto-color rule for ProgressBar (linear) — mockup ui-kit.jsx:202
 * @returns 'green' | 'amber' | 'red'
 */
export const autoRatioColor = (value, target) => {
  if (!target || target <= 0) return 'amber';
  const ratio = value / target;
  if (ratio >= 1) return 'green';
  if (ratio >= 0.8) return 'amber';
  return 'red';
};

/**
 * Auto-color rule for RingChart — mockup ui-kit.jsx:220
 * NOTE: differs from autoRatioColor — sub-80% returns 'cyan' (not 'red')
 * because the ring sub-100 default is "in progress" not "at risk".
 * @returns 'green' | 'amber' | 'cyan'
 */
export const autoRingColor = (value, target) => {
  if (!target || target <= 0) return 'amber';
  const ratio = value / target;
  if (ratio >= 1) return 'green';
  if (ratio >= 0.8) return 'amber';
  return 'cyan';
};

export const TCN_COLOR_MAP = {
  green: TCN.green,
  amber: TCN.amber,
  red: TCN.red,
  cyan: TCN.cyan,
  magenta: TCN.magenta,
  diamond: TCN.diamond,
};

export default TCN;
